import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { ValidatedInput } from '../../components/ValidatedInput';
import { validators } from '../../utils/validators';
import { useToast } from '../../components/Toast';
import { userApi } from '../../api/userApi';
import { isApiConfigured } from '../../api/axiosClient';

/**
 * Contact Us page — general customer-support contact form and org details.
 * Linked from the footer.
 */
const ContactUsPage = () => {
  const toast = useToast();
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: '',
    category: 'General Inquiry',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.name ||
      !form.email ||
      validators.email(form.email) ||
      !form.subject ||
      !form.message
    ) {
      toast.error('Please complete all required fields with valid information.', {
        title: 'Missing information',
      });
      return;
    }
      setSubmitting(true);
      if (isApiConfigured) {
          var res = await userApi.contact({
              name: form.name,
              email: form.email,
              subject: form.subject,
              message: form.message,
              category: form.category
          });

          if (res.success) {
              await new Promise((r) => setTimeout(r, 900));
              setSubmitting(false);
              setSubmitted(true);
              toast.success('Your message was sent. We\'ll reply within 1–2 business days.', {
                  title: 'Message sent',
              });
          }
          else {
              toast.success('Oops! Soemething went wrong... try again.', {
                  title: 'Message sent error',
              });
          }
      }
    // In real app, POST to /api/contact. Mock mode: simulate network delay.
    
  };

  if (submitted) {
    return (
      <div className="main-content">
        <div className="card" style={{ textAlign: 'center', padding: 56 }}>
          <CheckCircle size={56} color="#059669" strokeWidth={2} style={{ marginBottom: 16 }} />
          <h2 style={{ margin: '0 0 8px 0', color: '#1A1D21' }}>Message Received</h2>
          <p style={{ color: '#6B7280', marginBottom: 24 }}>
            Thanks for reaching out, {form.name}. Our team will respond to{' '}
            <strong style={{ color: '#1A1D21' }}>{form.email}</strong> within 1–2 business days.
          </p>
          <button
            className="btn-primary"
            onClick={() => {
              setSubmitted(false);
              setForm({ name: '', email: '', subject: '', category: 'General Inquiry', message: '' });
            }}
          >
            Send another message
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <Phone size={22} color="#0D9488" strokeWidth={2} />
            <h1 style={{ margin: 0 }}>Contact Us</h1>
          </div>
          <p>Get in touch with the Central Health Plans team</p>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          // auto-fit + minmax collapses to a single column below ~600px
          // viewport without needing a media query override on inline styles.
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 24,
        }}
      >
        {/* LEFT — Contact info */}
        <div className="card" style={{ padding: 28 }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: 18, fontWeight: 700, color: '#1A1D21' }}>
            Reach out directly
          </h3>

          <InfoRow
            icon={<Phone size={18} color="#0D9488" strokeWidth={2} />}
            label="Provider Services"
            value="512-978-8669"
            hint="Monday–Friday, 8 AM – 5 PM CT"
          />
          <InfoRow
            icon={<Mail size={18} color="#0D9488" strokeWidth={2} />}
            label="Email"
            value="chproviderservices@centralhealth.net"
            hint="Response within 1–2 business days"
          />
          <InfoRow
            icon={<MapPin size={18} color="#0D9488" strokeWidth={2} />}
            label="Mailing Address"
                      value="P. O. Box 17337,"
                      hint="Austin, TX 78760"
          />
          <InfoRow
            icon={<Clock size={18} color="#0D9488" strokeWidth={2} />}
            label="Business Hours"
            value="Monday – Friday"
            hint="8:00 AM – 5:00 PM Central Time"
          />

          <div
            style={{
              marginTop: 20,
              padding: 14,
              borderRadius: 12,
              background: 'rgba(13,148,136,0.06)',
              border: '1px solid rgba(13,148,136,0.15)',
              fontSize: 13,
              color: '#1A1D21',
              lineHeight: 1.5,
            }}
          >
            <strong>After hours?</strong> For urgent medical-emergency authorization
            support, our 24/7 nurse-advice line is available at{' '}
                      <strong>512-978-8669</strong>.
          </div>
        </div>

        {/* RIGHT — Contact form */}
        <div className="card" style={{ padding: 28 }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: 18, fontWeight: 700, color: '#1A1D21' }}>
            Send us a message
          </h3>

          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <ValidatedInput
                label="Your Name"
                required
                value={form.name}
                onChange={(v) => handleChange('name', v)}
                placeholder="Jane Smith"
                validate={(v) => validators.required(v, 'Name')}
              />
              <ValidatedInput
                label="Email"
                required
                type="email"
                value={form.email}
                onChange={(v) => handleChange('email', v)}
                placeholder="you@example.com"
                validate={(v) => validators.required(v, 'Email') || validators.email(v)}
              />
              <div className="form-group">
                <label className="required">Category</label>
                <select
                  value={form.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                >
                  <option>General Inquiry</option>
                  <option>Technical Support</option>
                  <option>Account Access</option>
                  <option>Billing Question</option>
                  <option>Claims Issue</option>
                  <option>Prior Authorization</option>
                  <option>Other</option>
                </select>
              </div>
              <ValidatedInput
                label="Subject"
                required
                value={form.subject}
                onChange={(v) => handleChange('subject', v)}
                placeholder="Brief subject line"
                validate={(v) => validators.required(v, 'Subject')}
              />
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="required">Message</label>
                <textarea
                  rows={6}
                  value={form.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  placeholder="Describe how we can help..."
                  style={{
                    width: '100%',
                    padding: 12,
                    borderRadius: 10,
                    border: '1.5px solid #E8ECF0',
                    fontFamily: 'inherit',
                    fontSize: 14,
                    resize: 'vertical',
                  }}
                />
              </div>
            </div>

            <div className="form-actions" style={{ marginTop: 16 }}>
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? (
                  'Sending...'
                ) : (
                  <>
                    <Send size={15} strokeWidth={2.5} style={{ marginRight: 6 }} />
                    Send Message
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const InfoRow = ({ icon, label, value, hint }) => (
  <div style={{ display: 'flex', gap: 14, marginBottom: 18, minWidth: 0 }}>
    <div
      style={{
        width: 38,
        height: 38,
        borderRadius: 10,
        background: 'rgba(13,148,136,0.08)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {icon}
    </div>
    {/* minWidth:0 allows this flex child to shrink below its intrinsic width
        so long emails/URLs wrap instead of overflowing on mobile. */}
    <div style={{ minWidth: 0, flex: 1 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </div>
      <div style={{
        fontSize: 15, fontWeight: 600, color: '#1A1D21', marginTop: 2,
        wordBreak: 'break-word', overflowWrap: 'anywhere',
      }}>
        {value}
      </div>
      {hint && (
        <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{hint}</div>
      )}
    </div>
  </div>
);

export default ContactUsPage;
